import React, { useEffect, useState } from "react";
import { Card, Row, Col, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";

import { setFilters } from "../AdvanceCustomerBalanceSlice";
import { fetchAdvanceCustomerBalance } from "../AdvanceCustomerBalanceThunk";
import {
  fetchCustomers,
  fetchLocations,
} from "../../../store/commonThunks/lookups";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";
import { useLocation, useSearchParams } from "react-router-dom";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import { useDefaultDateRange } from "../../../hooks/useDefaultDateRange";

const AdvanceCustomerBalanceFilters = () => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.advanceCustomerBalance);
  const lookups = useSelector((state) => state.lookups);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  useDefaultDateRange(setFilters, filters, "today");

    // handleFilterChange only queues the update, so running the report right here
    // would still use the old (default) filters — flag it and run it from the
    // next effect, once the drill-down filters have been applied.
    const [drillReady, setDrillReady] = useState(false);
    useEffect(() => {
      const path = pathname.split("/")[3];
      if (path) {
        handleFilterChange("filterType", "Date Wise");
        const fromDate = searchParams.get("fromDate");
        if (fromDate) {
          handleFilterChange("fromDate", fromDate);
        }
        handleFilterChange("toDate", path);
        setDrillReady(true);
      }
    }, []);
    useEffect(() => {
      if (drillReady) handleSubmit();
    }, [drillReady]);

  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchLocations());
  }, [dispatch]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleSubmit = () => {
    const { filterType, fromDate, toDate, accId, locationId } = filters;
    let payload = { filterType };
    if (filterType === "Debitor Wise") {
      payload.accId = accId || null;
    } else if (filterType === "Date Wise") {
      payload.fromDate = fromDate || null;
      payload.toDate = toDate || null;
    } else if (filterType === "Location Wise") {
      payload.locationId = locationId || null;
    }
    dispatch(fetchAdvanceCustomerBalance(payload));
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={16} style={{ alignItems: "center" }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All Debitor", display_text: "All Debitor" },
              { sale_id: "Debitor Wise", display_text: "Debitor Wise" },
              { sale_id: "Location Wise", display_text: "Location Wise" },
              { sale_id: "Date Wise", display_text: "Date Wise" },
            ]}
            value={{
              value: filters.filterType,
              label: filters.filterType,
            }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {filters.filterType === "Debitor Wise" && (
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <label>Customer / Debtor</label>
            <SearchListAutoComplete
              entityType="customer"
              optionsList={lookups.customers.map((c) => ({
                sale_id: c.acc_id,
                display_text: c.acc_name,
              }))}
              onSelect={(obj) => handleFilterChange("accId", obj?.sale_id)}
              placeholder="Select Customer"
              showAddNew={false}
            />
          </Col>
        )}

        {filters.filterType === "Location Wise" && (
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <label>Location</label>
            <SearchListAutoComplete
              entityType="location"
              optionsList={lookups.locations.map((l) => ({
                sale_id: l.location_id,
                display_text: l.location_name,
              }))}
              onSelect={(obj) => handleFilterChange("locationId", obj?.sale_id)}
              placeholder="Select Location"
              showAddNew={false}
            />
          </Col>
        )}

        {filters.filterType === "Date Wise" && (
          <CommonDateRangeFilter
            filters={filters}
            onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
            }}
          />
        )}

        <Col
          xs={24}
          sm={12}
          md={8}
          lg={6}
          xl={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSubmit}
            block
            data-testid="advance-customer-report-btn"
          >
            Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default AdvanceCustomerBalanceFilters;
